/*
 * Copyright (c) 2021 OTB Africa. All rights reserved. <https://otbafrica.com>
 *
 * Created Date: Wednesday, September 22nd 2021, 11:44:01 am
 * Author: OTB Africa, developers@otbafrica.com
 *
 * Use of this source code is governed by the license that can be found in
 * the LICENSE file or at https://developers.otbafrica.com/licenses/
 */

import React from "react";
import { STATUS_CODES } from "lib/api/types";
import { Organization, Portal, User } from "lib/schemas";
import { getBrowserObjectStorageFunctions } from "lib/utils";
import { useUserContext, IUserContext } from "authentication";
import { useAPIFetchDefaultOrganization, useAPIWorkerFetchOrganization } from "organizations/services/OrganizationsAPI";
import { SubscriptionProvider } from "saas-subscription/providers";

interface IGlobalStateLocalStorage {
  [key: User["id"]]: { selectedOrganizationId: Organization["id"] };
}

const getOrgIdFromBrowser = (loggedInUserId: User["id"]): Organization["id"] => {
  const { getObjectFromBrowserStorage } = getBrowserObjectStorageFunctions<IGlobalStateLocalStorage>();

  return getObjectFromBrowserStorage("onestop.scope.globalState")?.[loggedInUserId]?.selectedOrganizationId;
};

const saveOrgIdToBrowser = (loggedInUserId: User["id"], selectedOrganizationId: Organization["id"]): void => {
  const { getObjectFromBrowserStorage, saveObjectToBrowserStorage } =
    getBrowserObjectStorageFunctions<IGlobalStateLocalStorage>();

  const stateFromBrowser = getObjectFromBrowserStorage("onestop.scope.globalState");

  saveObjectToBrowserStorage("onestop.scope.globalState", {
    ...stateFromBrowser,
    [loggedInUserId]: { selectedOrganizationId: selectedOrganizationId },
  });
};

export interface IGlobalState {
  selectedOrganization?: Organization;
  isFetchingGlobalOrganization?: boolean;
  isGlobalOrganizationFetchError?: boolean;
  globalOrganizationFetchStatusCode?: number;
  selectedPortal?: Portal;
}

interface GlobalContext {
  globalState: IGlobalState;
  updateGlobalOrganizationScope: (organizationScope: UpdateOrganizationScopeArgs) => void;
  userContext: IUserContext;
}

export const GlobalScopeContext = React.createContext<GlobalContext>({
  globalState: undefined,
  updateGlobalOrganizationScope: undefined,
  userContext: undefined,
});

export const useGlobalScope = () => {
  return React.useContext(GlobalScopeContext);
};

interface UpdateOrganizationScopeArgs extends Partial<Pick<IGlobalState, "selectedOrganization">> {
  selectedOrganizationId?: Organization["id"];
}

export const GlobalScopeProvider = ({ children }) => {
  const [organizationFetchId, setOrganizationFetchId] = React.useState<Organization["id"]>();

  const [globalState, setGlobalState] = React.useState<IGlobalState>({});

  const userContext = useUserContext();
  const { loggedInUser } = userContext;

  useFetchGlobalOrganization({
    organizationIdToFetch: organizationFetchId ?? getOrgIdFromBrowser(loggedInUser?.id),
    loggedInUser,
    setGlobalState,
    globalState,
  });

  React.useEffect(() => {
    if (globalState.selectedOrganization?.id) {
      setOrganizationFetchId(globalState.selectedOrganization.id);
      saveOrgIdToBrowser(loggedInUser?.id, globalState.selectedOrganization.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [globalState.selectedOrganization?.id]);

  const updateGlobalOrganizationScope = ({
    selectedOrganization,
    selectedOrganizationId,
  }: UpdateOrganizationScopeArgs) => {
    if (selectedOrganization) {
      setGlobalState((prev) => ({ ...prev, selectedOrganization }));
      return;
    }

    setOrganizationFetchId(selectedOrganizationId);
  };

  const flushGlobalState = () => {
    setGlobalState({ selectedOrganization: null, selectedPortal: null });
    setOrganizationFetchId(null);
  };

  // Flush the global state if there is no loggedInUser i.e. User logs out.
  React.useEffect(() => {
    if (!loggedInUser) flushGlobalState();
  }, [loggedInUser]);

  return (
    <GlobalScopeContext.Provider value={{ globalState, updateGlobalOrganizationScope, userContext }}>
      <SubscriptionProvider organization={globalState?.selectedOrganization}>{children}</SubscriptionProvider>
    </GlobalScopeContext.Provider>
  );
};

interface UseFetchGlobalOrganization {
  organizationIdToFetch: Organization["id"];
  loggedInUser: User;
  setGlobalState: React.Dispatch<React.SetStateAction<IGlobalState>>;
  globalState: IGlobalState;
}

const useFetchGlobalOrganization = ({
  organizationIdToFetch,
  loggedInUser,
  setGlobalState,
  globalState,
}: UseFetchGlobalOrganization) => {
  const {
    organization: fetchedOrganization,
    isFetchingOrganization: isFetchingSelectedOrganization,
    fetchOrganization: fetchSelectedOrganization,
    isAPIError: isFetchSelectedOrgError,
    httpResponseStatusCode: fetchSelectedOrgStatusCode,
  } = useAPIWorkerFetchOrganization();

  const {
    apiDefaultOrganization,
    fetchDefaultOrganization,
    isFetchingDefaultOrganization,
    isAPIError: isFetchDefaultOrgError,
    httpResponseStatusCode: fetchDefaultOrgStatusCode,
  } = useAPIFetchDefaultOrganization();

  React.useEffect(() => {
    setGlobalState((prev) => ({
      ...prev,
      isFetchingGlobalOrganization: isFetchingSelectedOrganization || isFetchingDefaultOrganization,
      globalOrganizationFetchStatusCode:
        fetchSelectedOrgStatusCode >= STATUS_CODES.ClientError ? fetchSelectedOrgStatusCode : fetchDefaultOrgStatusCode,
      isGlobalOrganizationFetchError: isFetchSelectedOrgError || isFetchDefaultOrgError,
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFetchingSelectedOrganization, isFetchingDefaultOrganization]);

  React.useEffect(() => {
    if (!loggedInUser) return;

    if (organizationIdToFetch && globalState?.selectedOrganization?.id === organizationIdToFetch) return;

    if (organizationIdToFetch) {
      fetchSelectedOrganization(organizationIdToFetch);
      return;
    }

    fetchDefaultOrganization();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organizationIdToFetch, loggedInUser]);

  React.useEffect(() => {
    if (!fetchedOrganization?.id) return;

    setGlobalState((previousGlobalState) => ({ ...previousGlobalState, selectedOrganization: fetchedOrganization }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchedOrganization]);

  React.useEffect(() => {
    const availableStates: Organization["organization_status"][] = ["active", "pending"];

    const organizationIsAvailable = availableStates.includes(apiDefaultOrganization?.organization_status);

    // set organization scope to a default value triggered by the useDefaultOrganization API fetch.
    if (organizationIsAvailable && apiDefaultOrganization) {
      setGlobalState((previousGlobalState) => ({
        ...previousGlobalState,
        selectedOrganization: apiDefaultOrganization,
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiDefaultOrganization]);
};
